'use client';

import Link from 'next/link';

import { motion } from 'framer-motion';
import { Home, Sparkles } from 'lucide-react';

import { Button } from '@kit/ui/button';

/**
 * 404 Not Found Page
 *
 * Neon cyberpunk design with hexagonal wireframe, geometric constellation
 * decorations, glowing 404 text, and animated elements.
 */

/* ─── Hexagon vertex helpers ─── */
function hexPoint(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (Math.PI / 180) * angleDeg;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)] as const;
}

function hexPoints(cx: number, cy: number, r: number, offsetDeg = -30) {
  return Array.from({ length: 6 }, (_, i) =>
    hexPoint(cx, cy, r, offsetDeg + i * 60),
  );
}

function toPolygonStr(pts: (readonly [number, number])[]) {
  return pts.map(([x, y]) => `${x},${y}`).join(' ');
}

/* ─── Component ─── */
const NotFoundPage = () => {
  const cx = 250;
  const cy = 250;
  const outerR = 210;
  const innerR = 140;

  const outer = hexPoints(cx, cy, outerR, -30);
  const inner = hexPoints(cx, cy, innerR, 0); // rotated 30° for visual interest

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#0a0a0a] px-4">
      {/* ── Layer 1: Background constellation decorations ── */}
      <motion.svg
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 1200 800"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
        aria-hidden="true"
        animate={{ opacity: [0.3, 0.55, 0.3] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      >
        {/* Top-left cluster */}
        <g>
          <circle cx="80" cy="100" r="3" fill="#ec4899" opacity="0.6" />
          <circle cx="150" cy="60" r="2.5" fill="#3b82f6" opacity="0.5" />
          <circle cx="120" cy="160" r="2" fill="#f97316" opacity="0.5" />
          <circle cx="40" cy="180" r="3.5" fill="#d946ef" opacity="0.4" />
          <line
            x1="80"
            y1="100"
            x2="150"
            y2="60"
            stroke="#ec4899"
            strokeWidth="0.5"
            opacity="0.3"
          />
          <line
            x1="80"
            y1="100"
            x2="120"
            y2="160"
            stroke="#f97316"
            strokeWidth="0.5"
            opacity="0.3"
          />
          <line
            x1="120"
            y1="160"
            x2="40"
            y2="180"
            stroke="#d946ef"
            strokeWidth="0.5"
            opacity="0.25"
          />
          <line
            x1="150"
            y1="60"
            x2="120"
            y2="160"
            stroke="#3b82f6"
            strokeWidth="0.5"
            opacity="0.2"
          />
          <polygon
            points="60,50 110,30 90,90"
            stroke="#3b82f6"
            strokeWidth="0.5"
            opacity="0.2"
            fill="none"
          />
        </g>

        {/* Top-right cluster */}
        <g>
          <circle cx="1050" cy="80" r="3" fill="#ec4899" opacity="0.5" />
          <circle cx="1120" cy="130" r="2" fill="#3b82f6" opacity="0.4" />
          <circle cx="1080" cy="180" r="2.5" fill="#ec4899" opacity="0.5" />
          <circle cx="1150" cy="50" r="1.5" fill="#f97316" opacity="0.4" />
          <line
            x1="1050"
            y1="80"
            x2="1120"
            y2="130"
            stroke="#ec4899"
            strokeWidth="0.5"
            opacity="0.3"
          />
          <line
            x1="1120"
            y1="130"
            x2="1080"
            y2="180"
            stroke="#ec4899"
            strokeWidth="0.5"
            opacity="0.25"
          />
          <line
            x1="1050"
            y1="80"
            x2="1150"
            y2="50"
            stroke="#f97316"
            strokeWidth="0.5"
            opacity="0.2"
          />
          <polygon
            points="1100,40 1160,80 1130,120"
            stroke="#ec4899"
            strokeWidth="0.5"
            opacity="0.2"
            fill="none"
          />
        </g>

        {/* Bottom-left cluster */}
        <g>
          <circle cx="100" cy="650" r="2.5" fill="#ec4899" opacity="0.5" />
          <circle cx="60" cy="710" r="3" fill="#3b82f6" opacity="0.4" />
          <circle cx="160" cy="700" r="2" fill="#f97316" opacity="0.5" />
          <circle cx="130" cy="750" r="1.5" fill="#ec4899" opacity="0.4" />
          <line
            x1="100"
            y1="650"
            x2="60"
            y2="710"
            stroke="#ec4899"
            strokeWidth="0.5"
            opacity="0.3"
          />
          <line
            x1="60"
            y1="710"
            x2="160"
            y2="700"
            stroke="#3b82f6"
            strokeWidth="0.5"
            opacity="0.25"
          />
          <line
            x1="160"
            y1="700"
            x2="130"
            y2="750"
            stroke="#ec4899"
            strokeWidth="0.5"
            opacity="0.2"
          />
          <polygon
            points="50,660 120,640 80,720"
            stroke="#ec4899"
            strokeWidth="0.5"
            opacity="0.2"
            fill="none"
          />
        </g>

        {/* Bottom-right cluster */}
        <g>
          <circle cx="1080" cy="640" r="2.5" fill="#d946ef" opacity="0.5" />
          <circle cx="1140" cy="690" r="3" fill="#ec4899" opacity="0.4" />
          <circle cx="1060" cy="730" r="2" fill="#3b82f6" opacity="0.5" />
          <circle cx="1160" cy="750" r="1.5" fill="#f97316" opacity="0.3" />
          <line
            x1="1080"
            y1="640"
            x2="1140"
            y2="690"
            stroke="#d946ef"
            strokeWidth="0.5"
            opacity="0.3"
          />
          <line
            x1="1140"
            y1="690"
            x2="1060"
            y2="730"
            stroke="#3b82f6"
            strokeWidth="0.5"
            opacity="0.25"
          />
          <line
            x1="1060"
            y1="730"
            x2="1160"
            y2="750"
            stroke="#f97316"
            strokeWidth="0.5"
            opacity="0.2"
          />
          <line
            x1="1080"
            y1="640"
            x2="1060"
            y2="730"
            stroke="#ec4899"
            strokeWidth="0.5"
            opacity="0.2"
          />
        </g>
      </motion.svg>

      {/* ── Layer 2: Central content ── */}
      <div className="relative z-10 flex flex-col items-center text-center">
        {/* Hexagonal wireframe (behind text) */}
        <div className="absolute top-1/2 left-1/2 h-[280px] w-[280px] -translate-x-1/2 -translate-y-1/2 sm:h-[380px] sm:w-[380px] md:h-[500px] md:w-[500px]">
          <motion.svg
            viewBox="0 0 500 500"
            className="h-full w-full"
            aria-hidden="true"
            style={{
              filter:
                'drop-shadow(0 0 20px rgba(236,72,153,0.25)) drop-shadow(0 0 40px rgba(236,72,153,0.15))',
            }}
            animate={{ rotate: 360 }}
            transition={{
              duration: 60,
              repeat: Infinity,
              ease: 'linear',
            }}
          >
            {/* Outer hexagon */}
            <polygon
              points={toPolygonStr(outer)}
              stroke="#ec4899"
              strokeWidth="1.5"
              fill="none"
              opacity="0.3"
            />
            {/* Inner hexagon (rotated 30°) */}
            <polygon
              points={toPolygonStr(inner)}
              stroke="#ec4899"
              strokeWidth="1"
              fill="none"
              opacity="0.2"
            />
            {/* Wireframe connecting lines */}
            {outer.map(([ox, oy], i) => {
              const [ix, iy] = inner[i]!;
              return (
                <line
                  key={`wire-${i}`}
                  x1={ox}
                  y1={oy}
                  x2={ix}
                  y2={iy}
                  stroke="#db2777"
                  strokeWidth="0.6"
                  opacity="0.15"
                />
              );
            })}
            {/* Cross-connecting lines for 3D depth */}
            {outer.map(([ox, oy], i) => {
              const [ix, iy] = inner[(i + 1) % 6]!;
              return (
                <line
                  key={`cross-${i}`}
                  x1={ox}
                  y1={oy}
                  x2={ix}
                  y2={iy}
                  stroke="#db2777"
                  strokeWidth="0.4"
                  opacity="0.1"
                />
              );
            })}
          </motion.svg>
        </div>

        {/* Neon 404 text with glow */}
        <motion.div
          className="relative mb-4"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {/* Glow layer */}
          <motion.span
            className="text-eros-pink pointer-events-none absolute inset-0 flex items-center justify-center text-[8rem] leading-none font-extrabold blur-[30px] select-none sm:text-[10rem] md:text-[12rem]"
            aria-hidden="true"
            animate={{ opacity: [0.35, 0.55, 0.35] }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            404
          </motion.span>
          {/* Gradient text */}
          <h1 className="from-eros-pink via-eros-fuchsia to-eros-purple relative bg-gradient-to-br bg-clip-text text-[8rem] leading-none font-extrabold text-transparent select-none sm:text-[10rem] md:text-[12rem]">
            404
          </h1>
        </motion.div>

        {/* Divider line */}
        <motion.div
          className="from-eros-pink to-eros-purple mb-5 h-[2px] w-24 bg-gradient-to-r"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.3, duration: 0.5, ease: 'easeOut' }}
        />

        {/* Heading */}
        <motion.h2
          className="mb-3 text-2xl font-bold text-white sm:text-3xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          Page Not Found
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          className="mb-8 max-w-md text-sm text-gray-400 sm:text-base"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          Oops! The page you&apos;re looking for doesn&apos;t exist or has been
          moved.
        </motion.p>

        {/* Back to Home button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <Button
            asChild
            className="from-eros-pink to-eros-purple bg-gradient-to-r px-6 py-2.5 text-white hover:opacity-90"
          >
            <Link href="/">
              <Home className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </motion.div>
      </div>

      {/* ── Layer 3: Corner sparkle ── */}
      <motion.div
        className="text-eros-purple/40 absolute right-6 bottom-6 sm:right-8 sm:bottom-8"
        animate={{
          opacity: [0.3, 0.7, 0.3],
          scale: [1, 1.2, 1],
          rotate: [0, 15, 0],
        }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Sparkles className="h-6 w-6" />
      </motion.div>
    </div>
  );
};

export { NotFoundPage as NotFoundClient };
